import { DataLayer } from "@sazka/utils/data-layer";

function pushEvent(eventName: string) {
    DataLayer.push({ event: eventName });
}

export namespace BonusomatDatalayer {
    /*** https://sazka.atlassian.net/wiki/spaces/WA/pages/516947971/Bonusomat+v2.0+sazka.cz+hry ***/

    /*** 1.x – Measurement of specific pages ***/

    // 1.00 Game – Bonusomat page - Logged In
    export function pushMainPageShow() {
        DataLayer.push({
            event: "pageReady",
            page: {
                type: "game",
                kind: "game",
                gameCode: "bonusomat",
                gameCodeStrict: "bonusomat",
                verticalCode: "hry",
                verticalCodeStrict: "hry",
            },
        });
    }

    // 1.10 Game – Bonusomat page - Not Logged In
    export function pushLogoutPageShow() {
        DataLayer.push({
            page: {
                type: "game.notLoggedIn",
                kind: "game.notLoggedIn",
                gameCode: "bonusomat",
                gameCodeStrict: "bonusomat",
                verticalCode: "hry",
                verticalCodeStrict: "hry",
            },
            event: "pageReady",
        });
    }

    /*** 2.x – Measurement of actions ***/

    // 2.00 - Navigation click
    export function pushNavigationClick(completedTasks: number, remainingSpins: number) {
        DataLayer.pushInstant({
            event: "action.click.navigation",
            action: {
                click: {
                    name: "Bonusomat",
                    eventProperty1: "subHry",
                },
            },
            page: {
                completedTasks: `${completedTasks ?? 0}`,
                availableSpins: `${remainingSpins ?? 0}`,
            },
        });
    }

    // 2.10 - Lobby filter task list
    export function pushBoardFilterItemClick(filterItemLabel: string, completedTasks: number, remainingSpins: number) {
        DataLayer.push({
            event: "action.applyFilter.hry",
            action: {
                applyFilter: {
                    label: filterItemLabel,
                },
            },
            page: {
                completedTasks: `${completedTasks ?? 0}`,
                availableSpins: `${remainingSpins ?? 0}`,
            },
        });
    }

    // 2.20 - Start quest
    export function pushStartQuest(
        questName: string,
        eventSource: string,
        completedTasks: number,
        remainingSpins: number
    ) {
        DataLayer.push({
            event: "action.playQuest.hry",
            action: {
                playQuest: {
                    label: questName,
                    eventProperty1: eventSource,
                },
            },
            page: {
                completedTasks: `${completedTasks ?? 0}`,
                availableSpins: `${remainingSpins ?? 0}`,
            },
        });
    }

    // 2.25 - Open quest detail
    export function pushOpenQuest(questName: string, source: string, completedTasks: number, remainingSpins: number) {
        DataLayer.push({
            event: "action.openQuestDetail.hry",
            action: {
                openQuestDetail: {
                    label: questName,
                    eventProperty1: source,
                },
            },
            page: {
                completedTasks: `${completedTasks ?? 0}`,
                availableSpins: `${remainingSpins ?? 0}`,
            },
        });

        pushQuestDetail();
    }

    // 2.30 - More info click
    export function pushMoreInfoClick(eventName: string, completedTasks: number, remainingSpins: number) {
        DataLayer.pushInstant({
            event: "action.clickMoreInfo.hry",
            action: {
                clickMoreInfo: {
                    eventProperty1: eventName,
                },
            },
            page: {
                completedTasks: `${completedTasks ?? 0}`,
                availableSpins: `${remainingSpins ?? 0}`,
            },
        });
    }

    // 2.40 - Quest deactivation
    export function pushDeactivateQuest(eventSource: string, completedTasks: number, remainingSpins: number) {
        DataLayer.push({
            event: "action.deactivateQuest.hry",
            action: {
                deactivateQuest: {
                    eventProperty1: eventSource,
                },
            },
            page: {
                completedTasks: `${completedTasks ?? 0}`,
                availableSpins: `${remainingSpins ?? 0}`,
            },
        });
    }

    // 2.45 - Quest detail
    export function pushQuestDetail() {
        DataLayer.push({
            event: "page",
            page: {
                type: "game.questDetail",
                kind: "game.questDetail",
            },
        });
    }

    // 2.46 - Show winnings
    export function pushShowWinnings(questName: string) {
        DataLayer.pushInstant({
            event: "action.showQuestWinnings.hry",
            action: {
                showQuestWinnings: {
                    label: questName,
                },
            },
        });
    }

    // 2.50 - Quest deactivation
    export function pushClickWinRules(completedTasks: number, remainingSpins: number) {
        DataLayer.pushInstant({
            event: "action.clickWinRules.hry",
            page: {
                completedTasks: `${completedTasks ?? 0}`,
                availableSpins: `${remainingSpins ?? 0}`,
            },
        });
    }

    // 2.60 - Player clicks on "login" button on logout page
    export function pushLoginClick(eventSource: string) {
        DataLayer.push({
            event: "action.click.button",
            action: {
                click: {
                    label: "login",
                    eventProperty1: eventSource, // main||Ceny v losovacce
                },
            },
        });
    }

    /*** 3.x – Measurement of Promotions ***/

    // 3.00 - Onboarding carousel impression
    export function pushOnboardingImpression(stepTitle: string, stepIndex: number) {
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "hry.onboarding",
                            name: "hry.onboarding",
                            creative: stepTitle,
                            position: "hry.middle." + (stepIndex + 1),
                        },
                    ],
                },
            },
        });
    }

    // 3.10 - Onboarding carousel close
    export function pushOnboardingClose(stepTitle: string, stepIndex: number) {
        DataLayer.push({
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: "hry.onboarding.close",
                            name: "hry.onboarding",
                            creative: stepTitle,
                            position: "hry.middle." + (stepIndex + 1),
                        },
                    ],
                },
            },
        });
    }

    // 3.11 - Impress Quest - hry modal
    export function pushImpressQuestGames(questAndTaskName: string) {
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "hry.questGames",
                            name: "hry.questGames",
                            creative: questAndTaskName,
                            position: "hry.middle",
                        },
                    ],
                },
            },
        });
    }

    // 3.12 - Close Quest - hry modal
    export function pushCloseQuestGames(taskName: string) {
        DataLayer.push({
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: "hry.questGames.close",
                            name: "hry.questGames",
                            creative: taskName,
                            position: "hry.middle",
                        },
                    ],
                },
            },
        });
    }

    // 3.13 - Click on game - hry modal
    export function pushOpenQuestGame(taskName: string, position: number) {
        DataLayer.pushInstant({
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: "hry.questGames",
                            name: "hry.questGames",
                            creative: taskName,
                            position: "hry.middle." + position,
                        },
                    ],
                },
            },
        });
    }

    /*** Preserved from OLD Bonusomat https://sazka.atlassian.net/wiki/spaces/WA/pages/22872188/Bonusomat+sazka.cz+hry) ***/

    // 3.10 Successful spin
    export function pushSuccessfullSpin() {
        pushEvent("action.spinSuccess.bonusomat");
    }

    // 3.20 Spin error
    export function pushSpinError(errorMsg: string) {
        DataLayer.push({
            event: "action.spinError.bonusomat",
            action: {
                spinError: {
                    label: errorMsg,
                },
            },
        });
    }
}
